import { ZOHO_BOOKS_IMPORT_PATH } from './importRun';

/**
 * 🔒 Start the Documents Again (piece 31; decision 10470): before Sign off, one audited act on Settings › Integrations ›
 * Zoho Books removes everything the Zoho reads wrote on the document side and keeps every record blocks 1 to 3 wrote.
 * Dry Run answers what it would remove and keep and what blocks it, and writes nothing; Run takes the Backups page's
 * dump first and refuses when none succeeds, then removes it in one transaction.
 */
export const ZOHO_BOOKS_START_AGAIN_PATH = `${ZOHO_BOOKS_IMPORT_PATH}/start-again`;
export const ZOHO_BOOKS_START_AGAIN_DRY_RUN_PATH = `${ZOHO_BOOKS_START_AGAIN_PATH}/dry-run`;
export const ZOHO_BOOKS_START_AGAIN_RUN_PATH = `${ZOHO_BOOKS_START_AGAIN_PATH}/run`;

/** 🔒 The phrase the confirmation asks a person to type, and the run's route checks itself (decision 6500's pattern). */
export const START_AGAIN_PHRASE = 'START THE DOCUMENTS AGAIN';

/** The kinds the run removes, each counted, in the order the answer reads them. */
export const START_AGAIN_REMOVED_KINDS = [
  'quotes', 'salesOrders', 'purchaseOrders', 'invoices', 'creditNotes', 'payments', 'bills', 'vendorCredits',
  'journalEntries', 'journalLines', 'bankLines', 'stockMovements', 'attachments', 'sourceRefs', 'runs',
] as const;
export type StartAgainRemovedKind = (typeof START_AGAIN_REMOVED_KINDS)[number];

/** The kinds the run keeps, each counted, so the confirmation names what stays. */
export const START_AGAIN_KEPT_KINDS = ['companies', 'people', 'sites', 'employments', 'items', 'bankAccounts', 'accounts'] as const;
export type StartAgainKeptKind = (typeof START_AGAIN_KEPT_KINDS)[number];

/**
 * 🔒 What refuses the run while it stands (decision 10470): something a person added on top of an imported document,
 * an entry the run would remove dated in a Closed period, a read running or waiting, and Sign off done.
 */
export const START_AGAIN_BLOCKER_KINDS = [
  'signedOff', 'readInFlight', 'closedPeriod', 'refund', 'taxFiling', 'reconciliation', 'deposit', 'handApplied', 'note', 'linked',
] as const;
export type StartAgainBlockerKind = (typeof START_AGAIN_BLOCKER_KINDS)[number];

/** One kind of blocker: how many stand and the first few by name. */
export interface StartAgainBlocker {
  kind: StartAgainBlockerKind;
  count: number;
  named: string[];
}

/** The backup a run took first: its run, its time and the Mirror it went to. */
export interface StartAgainBackup {
  runId: string;
  at: string;
  target: string | null;
}

/** What a dry run would remove, keep and is blocked by, or what a run removed. */
export interface StartAgainAnswer {
  /** True on a dry run, which writes nothing. */
  dryRun: boolean;
  removed: Record<StartAgainRemovedKind, number>;
  kept: Record<StartAgainKeptKind, number>;
  /** Empty when nothing refuses the run. */
  blockers: StartAgainBlocker[];
  /** The backup a run took; null on a dry run. */
  backup: StartAgainBackup | null;
}

/** The documents the run removes, all eight kinds summed. */
export function startAgainDocuments(removed: Record<StartAgainRemovedKind, number>): number {
  return removed.quotes + removed.salesOrders + removed.purchaseOrders + removed.invoices + removed.creditNotes + removed.payments + removed.bills + removed.vendorCredits;
}
